import React, { useState, useEffect, useRef } from "react";
import { Lock, ArrowLeft, CheckCircle2, AlertCircle, Loader2, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { supabase } from "../supabaseClient";
import BizNearYouLogo from "./BizNearYouLogo";
import { validatePasswordReset } from "../utils/resetPasswordValidation";

interface ResetPasswordPageProps {
  onNavigate: (path: string) => void;
}

type RecoveryStatus = "checking" | "ready" | "invalid" | "expired" | "saving" | "success";

export function ResetPasswordPage({ onNavigate }: ResetPasswordPageProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus>("checking");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const recoveryInitializedRef = useRef(false);

  useEffect(() => {
    if (recoveryInitializedRef.current) return;
    recoveryInitializedRef.current = true;

    let authListenerUnsub: (() => void) | null = null;

    const initRecovery = async () => {
      try {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[Password Recovery] Recovery route reached:", window.location.pathname);
        }

        // 1. First check if Supabase already has an active authenticated session
        const { data: currentSessionData } = await supabase.auth.getSession();
        if (currentSessionData?.session) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("[Password Recovery] Active session found");
          }
          setRecoveryStatus("ready");
          cleanUrlParameters();
          return;
        }

        // 2. Inspect URL search query and hash parameters
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");

        const hashString = window.location.hash.replace(/^#/, "");
        const hashParams = new URLSearchParams(hashString);
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (process.env.NODE_ENV !== "production") {
          console.debug("[Password Recovery] Callback format:", code ? "code (PKCE)" : accessToken ? "hash (token)" : "session/listener");
        }

        // 3. Handle PKCE recovery link (?code=...)
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            // Re-check session in case auto-listener already exchanged it
            const { data: recheckSession } = await supabase.auth.getSession();
            if (recheckSession?.session) {
              setRecoveryStatus("ready");
              cleanUrlParameters();
              return;
            }
            setRecoveryStatus("invalid");
            setErrorMsg("This password-reset link is invalid or has expired. Please request a new password-reset email.");
            return;
          }
          if (data?.session) {
            setRecoveryStatus("ready");
            cleanUrlParameters();
            return;
          }
        }

        // 4. Handle Hash-based recovery link (#access_token=...&refresh_token=...)
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            const { data: recheckSession } = await supabase.auth.getSession();
            if (recheckSession?.session) {
              setRecoveryStatus("ready");
              cleanUrlParameters();
              return;
            }
            setRecoveryStatus("invalid");
            setErrorMsg("This password-reset link is invalid or has expired. Please request a new password-reset email.");
            return;
          }
          if (data?.session) {
            setRecoveryStatus("ready");
            cleanUrlParameters();
            return;
          }
        }

        // 5. Register listener for PASSWORD_RECOVERY or auth change
        const { data: subData } = supabase.auth.onAuthStateChange((event, session) => {
          if ((event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) {
            setRecoveryStatus("ready");
            cleanUrlParameters();
          }
        });
        authListenerUnsub = () => subData.subscription.unsubscribe();

        // Fallback check after short delay
        setTimeout(async () => {
          const { data: checkSession } = await supabase.auth.getSession();
          if (checkSession?.session) {
            setRecoveryStatus("ready");
            cleanUrlParameters();
          } else {
            setRecoveryStatus("invalid");
            setErrorMsg("This password-reset link is invalid or has expired. Please request a new password-reset email.");
          }
        }, 1200);

      } catch (err: any) {
        setRecoveryStatus("invalid");
        setErrorMsg("Failed to process password recovery link. Please request a new reset email.");
      }
    };

    initRecovery();

    return () => {
      if (authListenerUnsub) authListenerUnsub();
    };
  }, []);

  const cleanUrlParameters = () => {
    if (window.location.search || window.location.hash) {
      window.history.replaceState({}, document.title, "/reset-password");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recoveryStatus === "saving" || recoveryStatus === "success") return;

    setErrorMsg(null);

    const validation = validatePasswordReset(newPassword, confirmPassword);
    if (!validation.isValid) {
      const firstErr = validation.errors.password || validation.errors.confirmPassword;
      setErrorMsg(firstErr || "Please enter a valid matching password.");
      return;
    }

    setRecoveryStatus("saving");

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      setRecoveryStatus("success");
      setSuccessMsg("Your password has been reset successfully.");

      // Sign out temporary recovery session so user logs in fresh
      await supabase.auth.signOut();

    } catch (err: any) {
      setRecoveryStatus("ready");
      setErrorMsg(err?.message || "Failed to update password. Please check your connection and try again.");
    }
  };

  const isMinLength = newPassword.length >= 6;
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword;

  if (recoveryStatus === "checking") {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4">
        <div className="bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-slate-700/60 rounded-2xl p-8 shadow-xl max-w-sm w-full text-center space-y-4">
          <Loader2 className="w-8 h-8 text-indigo-600 dark:text-blue-500 animate-spin mx-auto" />
          <h2 className="text-base font-bold text-slate-900 dark:text-white">Verifying Recovery Link</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Please wait while we establish your secure password reset session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] text-slate-900 dark:text-slate-100 flex flex-col justify-center items-center p-4 md:p-6 font-sans relative overflow-hidden">
      {/* Background design accents */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 dark:bg-blue-900/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 dark:bg-emerald-900/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Back to Sign In button */}
      <button
        type="button"
        onClick={() => onNavigate("/login")}
        className="absolute top-6 left-6 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition flex items-center gap-1.5 font-semibold cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Sign In
      </button>

      <div className="max-w-md w-full bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-slate-700/60 rounded-2xl p-8 shadow-xl dark:shadow-2xl relative z-10 space-y-6">
        <div className="flex flex-col items-center text-center">
          <BizNearYouLogo textColor="text-slate-900 dark:text-white" className="scale-110 mb-4" />
          <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-3 shadow-sm">
            <KeyRound className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Reset your password
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1.5 max-w-xs leading-relaxed font-medium">
            Enter a strong new password for your BizNearYou account below.
          </p>
        </div>

        {errorMsg && (
          <div className="flex gap-2.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 rounded-xl p-3.5 text-xs text-rose-800 dark:text-rose-300">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
            <span className="leading-relaxed">{errorMsg}</span>
          </div>
        )}

        {recoveryStatus === "success" && successMsg && (
          <div className="space-y-5">
            <div className="flex gap-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 rounded-xl p-4 text-xs text-emerald-800 dark:text-emerald-300">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
              <div className="space-y-1">
                <span className="font-bold block text-sm">Success</span>
                <p className="leading-relaxed">{successMsg}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onNavigate("/login")}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-bold text-xs rounded-xl transition shadow-md cursor-pointer text-center flex items-center justify-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Continue to sign in</span>
            </button>
          </div>
        )}

        {(recoveryStatus === "ready" || recoveryStatus === "saving") && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-300 dark:border-slate-700 rounded-xl pl-10 pr-10 py-2.5 text-sm hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-slate-900 dark:text-white font-medium placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3.5 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition cursor-pointer"
                  title={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-300 dark:border-slate-700 rounded-xl pl-10 pr-10 py-2.5 text-sm hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-slate-900 dark:text-white font-medium placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3.5 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition cursor-pointer"
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Requirements Checklist */}
            <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3 space-y-1.5 border border-slate-200 dark:border-slate-700/50 text-[11px]">
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${isMinLength ? "text-emerald-500" : "text-slate-300 dark:text-slate-600"}`} />
                <span className={isMinLength ? "text-emerald-700 dark:text-emerald-400 font-semibold" : ""}>At least 6 characters long</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${isMatch ? "text-emerald-500" : "text-slate-300 dark:text-slate-600"}`} />
                <span className={isMatch ? "text-emerald-700 dark:text-emerald-400 font-semibold" : ""}>Passwords match</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={recoveryStatus === "saving" || !isMinLength || !isMatch}
              className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-bold py-2.5 px-4 rounded-xl text-xs transition tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {recoveryStatus === "saving" ? (
                <>
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                  <span>Saving new password...</span>
                </>
              ) : (
                <span>Reset password & save</span>
              )}
            </button>
          </form>
        )}

        {(recoveryStatus === "invalid" || recoveryStatus === "expired") && (
          <div className="space-y-3 pt-2">
            <button
              type="button"
              onClick={() => onNavigate("/forgot-password")}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl transition shadow-md cursor-pointer text-center"
            >
              Request a new password-reset email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
